import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import type { AttributeType } from '@messe/shared'
import { Button } from '../components/ui/button.js'
import { CheckboxField, SelectField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { format, t } from '../i18n/index.js'
import { useAuth } from '../lib/auth.js'
import { listAttributes, type AttributeView } from '../lib/attributes.js'
import { listEvents } from '../lib/events.js'
import { createItem, deleteItem, getItem, restoreItem, updateItem } from '../lib/items.js'
import { listValueListItems, listValueLists } from '../lib/value-lists.js'

/**
 * One captured article: what it holds, its photographs, and the controls to
 * correct it.
 *
 * The form is built from the catalogue, exactly as the capture form on the
 * phone is. A field added through the designer appears here without anybody
 * touching this file — which is the same promise, kept in a second place.
 */
export function ItemDetailPage({ mode }: { mode: 'view' | 'create' }) {
  const strings = t().items
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()

  const params: { itemId?: string } = useParams({ strict: false })
  const itemId = mode === 'create' ? undefined : params.itemId

  const [values, setValues] = useState<Record<string, unknown>>({})
  const [barcode, setBarcode] = useState('')
  const [supplierItemId, setSupplierItemId] = useState('')
  const [eventId, setEventId] = useState('')
  const [needsReview, setNeedsReview] = useState(false)
  const [editing, setEditing] = useState(mode === 'create')
  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const item = useQuery({
    queryKey: ['item', itemId],
    // skipToken rather than `enabled`: it narrows the id for the type checker
    // instead of leaving an assertion that says "trust me".
    queryFn: itemId === undefined ? skipToken : () => getItem(itemId),
  })

  const attributes = useQuery({ queryKey: ['attributes', false], queryFn: () => listAttributes() })
  const events = useQuery({ queryKey: ['events'], queryFn: () => listEvents() })
  const valueLists = useQuery({ queryKey: ['value-lists'], queryFn: listValueLists })

  // Loading the saved values into the form once they arrive. Guarded on the
  // revision rather than on the object, because a refetch produces a new object
  // every time and would throw away whatever was being typed.
  useEffect(() => {
    if (!item.data) return
    setValues(item.data.attributes)
    setBarcode(item.data.barcode ?? '')
    setSupplierItemId(item.data.supplierItemId ?? '')
    setNeedsReview(item.data.needsReview)
  }, [item.data?.id, item.data?.revision])

  /*
   * A new article starts from the catalogue's defaults.
   *
   * Booleans get `false` rather than nothing. An unticked checkbox reads as
   * "no", and leaving the value absent instead makes the server refuse a
   * required field the person is looking straight at — with a message about a
   * field they would swear they had answered.
   */
  const catalogueLoaded = attributes.data !== undefined
  useEffect(() => {
    if (mode !== 'create' || !attributes.data) return

    const defaults: Record<string, unknown> = {}
    for (const field of attributes.data) {
      if (!field.active || field.auto || field.storage !== 'jsonb') continue
      if (field.defaultValue !== null && field.defaultValue !== undefined) {
        defaults[field.key] = field.defaultValue
      } else if (field.type === 'boolean') {
        defaults[field.key] = false
      }
    }
    setValues(defaults)
  }, [mode, catalogueLoaded])

  const save = useMutation({
    mutationFn: async () => {
      if (mode === 'create') {
        return createItem({
          eventId,
          barcode: barcode === '' ? null : barcode,
          supplierItemId: supplierItemId === '' ? null : supplierItemId,
          attributes: values,
          clientCreatedAt: null,
        })
      }

      const loaded = item.data
      if (itemId === undefined || loaded === undefined) {
        // Only reachable if the save button appeared before the article
        // finished loading, which it does not — but sending an update without
        // a base revision would silently overwrite whatever arrived meanwhile,
        // and that is worth a guard rather than an assertion.
        throw new Error(strings.notFound)
      }

      return updateItem(itemId, {
        barcode: barcode === '' ? null : barcode,
        supplierItemId: supplierItemId === '' ? null : supplierItemId,
        attributes: values,
        needsReview,
        // The revision the form was loaded from. The server refuses the write
        // if a device changed the article in between.
        baseRevision: loaded.revision,
      })
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['items'] })
      await queryClient.invalidateQueries({ queryKey: ['item'] })
      setProblem(null)

      if (mode === 'create') {
        await navigate({ to: '/artikel/$itemId', params: { itemId: saved.id } })
        return
      }

      setEditing(false)
      setNotice(strings.saved)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const remove = useMutation({
    mutationFn: async () => {
      if (itemId === undefined) throw new Error(strings.notFound)
      await deleteItem(itemId)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['items'] })
      await queryClient.invalidateQueries({ queryKey: ['item'] })
      setNotice(strings.deletedNotice)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const restore = useMutation({
    mutationFn: async () => {
      if (itemId === undefined) throw new Error(strings.notFound)
      return restoreItem(itemId)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['items'] })
      await queryClient.invalidateQueries({ queryKey: ['item'] })
      setNotice(strings.restoredNotice)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  // Only jsonb-backed fields. Barcode and supplier live in columns of their
  // own and have their own controls above; rendered from the catalogue as well
  // they would appear a second time, always empty, because the form reads them
  // out of the attributes and their values were never in there. Auto fields and
  // photos are filled by the device, never typed.
  const fields = (attributes.data ?? [])
    .filter(
      (field) => field.active && !field.auto && field.storage === 'jsonb' && field.type !== 'photo',
    )
    .sort((a, b) => a.sortOrder - b.sortOrder)

  if (mode === 'view' && item.isPending) {
    return <p className="text-text-muted text-sm">{t().app.loading}</p>
  }

  if (mode === 'view' && item.isError) {
    return <Alert tone="danger">{strings.notFound}</Alert>
  }

  const data = item.data
  const isDeleted = data?.deletedAt != null
  const canEdit = hasRole('admin') && !isDeleted

  return (
    <div className="space-y-6">
      <div>
        <Link to="/artikel" className="text-accent text-sm hover:underline">
          ← {strings.backToList}
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold">
            {mode === 'create' ? strings.create : (data?.shortCode ?? strings.noShortCode)}
            {data?.needsReview === true && <Badge tone="warning">{strings.needsReview}</Badge>}
            {isDeleted && <Badge tone="danger">{strings.deleted}</Badge>}
          </h1>
          {data && (
            <p className="text-text-muted text-sm">
              {format(strings.capturedBy, {
                event: `${data.eventCode} — ${data.eventName}`,
                person: data.createdByName ?? '—',
                date: new Date(data.clientCreatedAt).toLocaleString('de-DE'),
              })}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {mode === 'view' && canEdit && !editing && (
            <Button onClick={() => setEditing(true)}>{strings.edit}</Button>
          )}
          {mode === 'view' && canEdit && !editing && (
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(strings.deleteConfirm)) remove.mutate()
              }}
            >
              {strings.delete}
            </Button>
          )}
          {mode === 'view' && hasRole('admin') && isDeleted && (
            <Button disabled={restore.isPending} onClick={() => restore.mutate()}>
              {strings.restore}
            </Button>
          )}
          {editing && (
            <>
              {mode === 'view' && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(false)
                    if (data) {
                      setValues(data.attributes)
                      setBarcode(data.barcode ?? '')
                      setSupplierItemId(data.supplierItemId ?? '')
                    }
                  }}
                >
                  {strings.cancel}
                </Button>
              )}
              <Button
                disabled={save.isPending || (mode === 'create' && eventId === '')}
                onClick={() => save.mutate()}
              >
                {save.isPending ? strings.saving : strings.save}
              </Button>
            </>
          )}
        </div>
      </header>

      {notice !== null && <Alert tone="success">{notice}</Alert>}
      {problem !== null && <Alert tone="danger">{problem}</Alert>}
      {isDeleted && <Alert tone="warning">{strings.deletedHint}</Alert>}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <section className="border-border bg-surface-raised space-y-4 rounded-lg border p-4">
          <h2 className="text-lg font-semibold">{strings.values}</h2>

          {mode === 'create' && (
            <SelectField
              label={strings.event}
              hint={strings.eventHint}
              value={eventId}
              options={[
                { value: '', label: strings.chooseEvent },
                ...(events.data ?? []).map((event) => ({
                  value: event.id,
                  label: `${event.code} — ${event.name}`,
                })),
              ]}
              onChange={(event) => setEventId(event.target.value)}
            />
          )}

          <TextField
            label={strings.barcode}
            value={barcode}
            disabled={!editing}
            onChange={(event) => setBarcode(event.target.value)}
          />

          <SupplierField
            valueListId={valueLists.data?.find((list) => list.key === 'suppliers')?.id}
            value={supplierItemId}
            disabled={!editing}
            onChange={setSupplierItemId}
          />

          {fields.map((field) => (
            <AttributeField
              key={field.id}
              field={field}
              value={values[field.key]}
              disabled={!editing}
              onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))}
            />
          ))}

          {mode === 'view' && (
            <CheckboxField
              label={strings.needsReviewLabel}
              hint={strings.needsReviewHint}
              checked={needsReview}
              disabled={!editing}
              onChange={(event) => setNeedsReview(event.target.checked)}
            />
          )}
        </section>

        <section className="border-border bg-surface-raised space-y-4 rounded-lg border p-4">
          <h2 className="text-lg font-semibold">{strings.photos}</h2>

          {mode === 'create' ? (
            <p className="text-text-muted text-sm">{strings.photosOnDevice}</p>
          ) : (data?.photos.length ?? 0) === 0 ? (
            <p className="text-text-muted text-sm">{strings.noPhotos}</p>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="item-photos">
              {(data?.photos ?? []).map((photo) => (
                <figure key={photo.id} className="space-y-1">
                  {photo.url === null ? (
                    <div className="border-border text-text-muted flex aspect-square items-center justify-center rounded border border-dashed text-xs">
                      {strings.photoPending}
                    </div>
                  ) : (
                    <a href={photo.url} target="_blank" rel="noreferrer">
                      <img
                        src={photo.url}
                        alt={photo.attributeKey}
                        loading="lazy"
                        className="border-border aspect-square w-full rounded border object-cover"
                      />
                    </a>
                  )}
                  <figcaption className="text-text-muted text-xs">
                    {photo.width !== null && photo.height !== null
                      ? `${String(photo.width)}×${String(photo.height)}`
                      : photo.attributeKey}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}

          {data && (
            <dl className="border-border space-y-1 border-t pt-4 text-sm">
              <Row label={strings.schemaVersion} value={String(data.schemaVersion)} />
              <Row label={strings.revision} value={String(data.revision)} />
              <Row
                label={strings.updatedAt}
                value={new Date(data.updatedAt).toLocaleString('de-DE')}
              />
            </dl>
          )}
        </section>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-text-muted">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function SupplierField({
  valueListId,
  value,
  disabled,
  onChange,
}: {
  valueListId: string | undefined
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const strings = t().items
  const entries = useQuery({
    queryKey: ['value-list-items', valueListId, 'all'],
    queryFn:
      valueListId === undefined ? skipToken : () => listValueListItems(valueListId, { limit: 200 }),
  })

  return (
    <SelectField
      label={strings.supplier}
      value={value}
      disabled={disabled}
      options={[
        { value: '', label: strings.noSupplier },
        ...(entries.data?.items ?? []).map((entry) => ({ value: entry.id, label: entry.label })),
      ]}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/**
 * One field of the catalogue, rendered by its type.
 *
 * Types with no sensible single control — multiselect, barcode-as-a-field —
 * fall back to a text input rather than being hidden. Hiding a field would mean
 * a value captured on the phone could never be corrected in the portal, and the
 * person looking at it would have no idea it was there.
 */
function AttributeField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: AttributeView
  value: unknown
  disabled: boolean
  onChange: (value: unknown) => void
}) {
  const listId = field.valueListId
  const entries = useQuery({
    queryKey: ['value-list-items', listId, 'all'],
    queryFn:
      listId === null || !isSelect(field.type)
        ? skipToken
        : () => listValueListItems(listId, { limit: 200 }),
  })

  const hint = field.helpText ?? undefined

  if (field.type === 'boolean') {
    return (
      <CheckboxField
        label={field.label}
        {...(hint ? { hint } : {})}
        checked={value === true}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    )
  }

  if (field.type === 'select') {
    return (
      <SelectField
        label={field.label}
        {...(hint ? { hint } : {})}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        options={[
          { value: '', label: '—' },
          ...(entries.data?.items ?? []).map((entry) => ({ value: entry.id, label: entry.label })),
        ]}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      />
    )
  }

  const numeric = field.type === 'number' || field.type === 'decimal' || field.type === 'integer'

  return (
    <TextField
      label={field.label}
      {...(hint ? { hint } : {})}
      type={numeric ? 'number' : field.type === 'date' ? 'date' : 'text'}
      value={asText(value)}
      disabled={disabled}
      onChange={(event) => {
        const raw = event.target.value
        if (raw === '') {
          onChange(null)
          return
        }
        onChange(numeric ? Number(raw) : raw)
      }}
    />
  )
}

function isSelect(type: AttributeType): boolean {
  return type === 'select' || type === 'multiselect'
}

/** A captured value as text. Objects are shown as JSON rather than as [object Object]. */
function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}
